import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Edit, Trash2, ChevronUp, ChevronDown, Star } from "lucide-react";
import { getApiErrorPayload, orderTypesAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { Loading } from "@/components/ui/loader";
import { OrderTypeIcon, ORDER_TYPE_ICON_KEYS, ORDER_TYPE_COLORS } from "./order-type-icons";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface OrderType {
	id: number;
	nameEn: string;
	nameAr: string;
	icon: string | null;
	color: string | null;
	isActive: boolean;
	isDefault: boolean;
	sortOrder: number;
	branchId: number;
}

interface OrderTypeForm {
	nameEn: string;
	nameAr: string;
	icon: string;
	color: string;
	isActive: boolean;
	isDefault: boolean;
}

const emptyForm: OrderTypeForm = {
	nameEn: "",
	nameAr: "",
	icon: "utensils",
	color: ORDER_TYPE_COLORS[0],
	isActive: true,
	isDefault: false,
};

/** Branch-scoped order-type CRUD (name AR/EN, icon, colour, active, default, reorder). Embedded in the POS detail page. */
export function OrderTypesManager({ branchId }: { branchId: number }) {
	const { t, i18n } = useTranslation();
	const isAr = i18n.language === "ar";
	const { toast } = useToast();
	const [items, setItems] = useState<OrderType[]>([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<OrderType | null>(null);
	const [form, setForm] = useState<OrderTypeForm>(emptyForm);
	const [saving, setSaving] = useState(false);

	const showError = useCallback(
		(error: unknown) => {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		},
		[t, toast],
	);

	const fetchItems = useCallback(() => {
		setLoading(true);
		orderTypesAPI
			.getAll(branchId)
			.then((data: OrderType[]) => setItems(data))
			.catch(showError)
			.finally(() => setLoading(false));
	}, [branchId, showError]);

	useEffect(() => {
		fetchItems();
	}, [fetchItems]);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setDialogOpen(true);
	};

	const openEdit = (ot: OrderType) => {
		setEditing(ot);
		setForm({
			nameEn: ot.nameEn,
			nameAr: ot.nameAr,
			icon: ot.icon ?? "utensils",
			color: ot.color ?? ORDER_TYPE_COLORS[0],
			isActive: ot.isActive,
			isDefault: ot.isDefault,
		});
		setDialogOpen(true);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSaving(true);
		const payload = {
			branchId,
			nameEn: form.nameEn.trim(),
			nameAr: form.nameAr.trim(),
			icon: form.icon || undefined,
			color: form.color || undefined,
			isActive: form.isActive,
			isDefault: form.isDefault,
		};
		try {
			if (editing) {
				await orderTypesAPI.update(editing.id, payload);
				toast({ title: t("common.success"), description: t("orderTypesAdmin.messages.updated") });
			} else {
				await orderTypesAPI.create(payload);
				toast({ title: t("common.success"), description: t("orderTypesAdmin.messages.created") });
			}
			setDialogOpen(false);
			fetchItems();
		} catch (error) {
			showError(error);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (ot: OrderType) => {
		if (!(await confirmAction(t("orderTypesAdmin.confirmDelete", { name: isAr ? ot.nameAr : ot.nameEn }), { confirmLabel: t("common.delete") }))) return;
		try {
			await orderTypesAPI.delete(ot.id);
			toast({ title: t("common.success"), description: t("orderTypesAdmin.messages.deleted") });
			fetchItems();
		} catch (error) {
			showError(error);
		}
	};

	const move = async (index: number, dir: -1 | 1) => {
		const next = index + dir;
		if (next < 0 || next >= items.length) return;
		const reordered = [...items];
		[reordered[index], reordered[next]] = [reordered[next], reordered[index]];
		setItems(reordered);
		try {
			await orderTypesAPI.reorder(
				branchId,
				reordered.map(o => o.id),
			);
		} catch (error) {
			showError(error);
			fetchItems();
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-end gap-2">
				<PermissionGate permission="settings.create">
					<Button type="button" size="sm" onClick={openCreate}>
						<Plus className="me-2 h-4 w-4" />
						{t("orderTypesAdmin.add")}
					</Button>
				</PermissionGate>
			</div>

			{loading ? (
				<Loading />
			) : items.length === 0 ? (
				<p className="text-muted-foreground py-8 text-center text-sm">{t("orderTypesAdmin.empty")}</p>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-24">{t("orderTypesAdmin.table.order")}</TableHead>
							<TableHead>{t("orderTypesAdmin.table.name")}</TableHead>
							<TableHead className="text-center">{t("orderTypesAdmin.table.default")}</TableHead>
							<TableHead className="text-center">{t("orderTypesAdmin.table.status")}</TableHead>
							<TableHead className="text-end">{t("orderTypesAdmin.table.actions")}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{items.map((ot, index) => (
							<TableRow key={ot.id}>
								<TableCell>
									<div className="flex items-center gap-1">
										<Button
											title={t("orderTypesAdmin.moveUp")}
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											disabled={index === 0}
											onClick={() => move(index, -1)}
											aria-label={t("orderTypesAdmin.moveUp")}
										>
											<ChevronUp className="h-4 w-4" />
										</Button>
										<Button
											title={t("orderTypesAdmin.moveDown")}
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											disabled={index === items.length - 1}
											onClick={() => move(index, 1)}
											aria-label={t("orderTypesAdmin.moveDown")}
										>
											<ChevronDown className="h-4 w-4" />
										</Button>
									</div>
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<span
											className="flex h-8 w-8 items-center justify-center rounded-md text-white"
											style={{ backgroundColor: ot.color ?? "#64748b" }}
										>
											<OrderTypeIcon icon={ot.icon} className="h-4 w-4" />
										</span>
										<div className="leading-tight">
											<div className="font-semibold">{isAr ? ot.nameAr : ot.nameEn}</div>
											<div className="text-muted-foreground text-xs">{isAr ? ot.nameEn : ot.nameAr}</div>
										</div>
									</div>
								</TableCell>
								<TableCell className="text-center">
									{ot.isDefault ? (
										<Badge variant="default" className="gap-1">
											<Star className="h-3 w-3" />
											{t("orderTypesAdmin.default")}
										</Badge>
									) : (
										<span className="text-muted-foreground">-</span>
									)}
								</TableCell>
								<TableCell className="text-center">
									<Badge variant={ot.isActive ? "success" : "secondary"}>
										{ot.isActive ? t("orderTypesAdmin.active") : t("orderTypesAdmin.inactive")}
									</Badge>
								</TableCell>
								<TableCell className="text-end">
									<div className="flex justify-end gap-1">
										<PermissionGate permission="settings.update">
											<Button
												title={t("common.edit")}
												aria-label={t("common.edit")}
												variant="ghost"
												size="icon"
												onClick={() => openEdit(ot)}
											>
												<Edit className="h-4 w-4" />
											</Button>
										</PermissionGate>
										<PermissionGate permission="settings.delete">
											<Button
												title={t("common.delete")}
												aria-label={t("common.delete")}
												variant="ghost"
												size="icon"
												className="text-destructive"
												onClick={() => handleDelete(ot)}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</PermissionGate>
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{editing ? t("orderTypesAdmin.editTitle") : t("orderTypesAdmin.add")}</DialogTitle>
						<DialogDescription>{t("orderTypesAdmin.subtitle")}</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="ot-nameEn">{t("orderTypesAdmin.form.nameEn")}</Label>
								<Input
									id="ot-nameEn"
									value={form.nameEn}
									onChange={e => setForm({ ...form, nameEn: e.target.value })}
									placeholder={t("orderTypesAdmin.form.nameEnPlaceholder")}
									required
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="ot-nameAr">{t("orderTypesAdmin.form.nameAr")}</Label>
								<Input
									id="ot-nameAr"
									value={form.nameAr}
									onChange={e => setForm({ ...form, nameAr: e.target.value })}
									placeholder={t("orderTypesAdmin.form.nameArPlaceholder")}
									dir="rtl"
									required
								/>
							</div>
						</div>

						<div className="space-y-2">
							<Label>{t("orderTypesAdmin.form.icon")}</Label>
							<div className="flex flex-wrap gap-2">
								{ORDER_TYPE_ICON_KEYS.map(key => (
									<button
										title={key}
										key={key}
										type="button"
										onClick={() => setForm({ ...form, icon: key })}
										aria-label={key}
										className={`flex h-10 w-10 items-center justify-center rounded-md border transition ${
											form.icon === key ? "border-primary ring-primary ring-2" : "border-input"
										}`}
										style={form.icon === key ? { backgroundColor: form.color, color: "#fff" } : undefined}
									>
										<OrderTypeIcon icon={key} className="h-5 w-5" />
									</button>
								))}
							</div>
						</div>

						<div className="space-y-2">
							<Label>{t("orderTypesAdmin.form.color")}</Label>
							<div className="flex flex-wrap items-center gap-2">
								{ORDER_TYPE_COLORS.map(c => (
									<button
										key={c}
										type="button"
										onClick={() => setForm({ ...form, color: c })}
										aria-label={c}
										className={`h-8 w-8 rounded-full border-2 transition ${
											form.color === c ? "ring-primary scale-110 ring-2" : "border-transparent"
										}`}
										style={{ backgroundColor: c }}
									/>
								))}
								<Input
									type="color"
									value={form.color}
									onChange={e => setForm({ ...form, color: e.target.value })}
									className="h-8 w-12 p-1"
									aria-label={t("orderTypesAdmin.form.customColor")}
								/>
							</div>
						</div>

						<div className="flex items-center justify-between rounded-md border p-3">
							<div>
								<Label htmlFor="ot-isDefault">{t("orderTypesAdmin.form.isDefault")}</Label>
								<p className="text-muted-foreground text-xs">{t("orderTypesAdmin.form.isDefaultHint")}</p>
							</div>
							<Switch
								id="ot-isDefault"
								checked={form.isDefault}
								onCheckedChange={checked => setForm({ ...form, isDefault: checked })}
							/>
						</div>

						<div className="flex items-center justify-between rounded-md border p-3">
							<Label htmlFor="ot-isActive">{t("orderTypesAdmin.form.isActive")}</Label>
							<Switch
								id="ot-isActive"
								checked={form.isActive}
								onCheckedChange={checked => setForm({ ...form, isActive: checked })}
							/>
						</div>

						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
								{t("common.cancel")}
							</Button>
							<Button type="submit" disabled={saving}>
								{editing ? t("common.update") : t("common.create")}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
